import React, { useEffect, useState } from 'react';
import { Send, ArrowRight, CheckCircle } from 'lucide-react';

const Waitlist = ({ defaultProgram = '', onSuccess }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    program: defaultProgram
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      program: defaultProgram
    }));
  }, [defaultProgram]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    
    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitted(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setSubmitError('Failed to join the waitlist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="w-full max-w-md md:max-w-lg mx-auto relative">
        {/* Elegant border animation */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-3xl animate-pulse"></div>
        <div className="absolute inset-[1px] bg-black/80 backdrop-blur-2xl rounded-3xl"></div>
        
        {/* Diagonal accent lines */}
        <div className="absolute top-8 left-8 w-16 h-px bg-gradient-to-r from-white/60 to-transparent"></div>
        <div className="absolute bottom-8 right-8 w-16 h-px bg-gradient-to-l from-white/60 to-transparent"></div>
        
        <div className="relative z-10 p-8 md:p-12 flex flex-col items-center justify-center text-center">
          <div className="mb-6 relative">
            <div className="absolute inset-0 bg-green-400/20 rounded-full blur-xl animate-pulse"></div>
            <CheckCircle className="text-green-400 relative z-10 animate-bounce" size={56} />
          </div>
          
          <h2 className="text-white text-2xl md:text-3xl font-thin mb-3 tracking-wide">
            <span className="italic font-light">Merci</span> for joining
          </h2>
          <div className="w-16 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent mb-4"></div>
          <p className="text-white/70 text-sm md:text-base leading-relaxed tracking-wide">
            You'll be the first to know when enrollment opens.
            <br />
            <em className="text-white/50 text-xs">Check your email for confirmation.</em>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md md:max-w-lg mx-auto relative">
      {/* Animated border frame */}
      <div className={`absolute inset-0 transition-all duration-2000 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent rounded-3xl"></div>
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-px bg-gradient-to-l from-transparent via-white/30 to-transparent"></div>
        <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-white/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-px h-full bg-gradient-to-t from-transparent via-white/30 to-transparent"></div>
      </div>
      
      {/* Main form container */}
      <div className="relative z-10 bg-black/70 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 md:p-10 shadow-2xl">
        {/* Decorative elements */}
        <div className="absolute top-6 left-6 w-8 h-px bg-gradient-to-r from-white/40 to-transparent"></div>
        <div className="absolute bottom-6 right-6 w-8 h-px bg-gradient-to-l from-white/40 to-transparent"></div>
        
        {/* Header */}
        <div className={`text-center mb-8 transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'
        }`}>
          <p className="text-white/50 text-[10px] md:text-xs tracking-[0.3em] uppercase mb-2">
            Exclusive Access
          </p>
          <h1 className="text-white text-2xl md:text-3xl font-thin mb-3 tracking-wide">
            Join the <em className="italic font-light">Waitlist</em>
          </h1>
          <div className="w-12 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent mx-auto mb-3"></div>
          <p className="text-white/60 text-sm md:text-base leading-relaxed tracking-wide">
            Be first to know when enrollment opens.
          </p>
        </div>

        {/* Status messages */}
        {submitting && (
          <div className="text-center text-white/80 bg-black/40 border border-white/10 rounded-xl py-3 mb-4 text-sm tracking-wide">
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white/20 border-t-white/80 rounded-full animate-spin"></div>
              Submitting your request...
            </div>
          </div>
        )}
        
        {submitError && (
          <div className="text-center text-red-300 bg-red-900/20 border border-red-500/20 rounded-xl py-3 mb-4 text-sm tracking-wide">
            {submitError}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Full Name */}
          <div className={`relative transition-all duration-800 delay-500 ${
            isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'
          }`}>
            <input
              id="fullName"
              name="fullName"
              type="text"
              value={formData.fullName}
              onChange={handleInputChange}
              onFocus={() => setFocusedField('fullName')}
              onBlur={() => setFocusedField('')}
              required
              placeholder="Full Name*"
              className={`w-full bg-white/5 border rounded-xl px-5 py-3 md:py-4 text-white text-base placeholder-white/30 focus:outline-none transition-all duration-500 ${
                focusedField === 'fullName' 
                  ? 'border-white/40 bg-white/10 shadow-lg' 
                  : 'border-white/10 hover:border-white/20'
              }`}
            />
            <div className={`absolute bottom-0 left-0 h-px bg-gradient-to-r from-white/40 to-transparent transition-all duration-500 ${
              focusedField === 'fullName' ? 'w-full' : 'w-0'
            }`}></div>
          </div>

          {/* Phone */}
          <div className={`relative transition-all duration-800 delay-600 ${
            isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'
          }`}>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              onFocus={() => setFocusedField('phone')}
              onBlur={() => setFocusedField('')}
              required
              placeholder="Phone Number*"
              className={`w-full bg-white/5 border rounded-xl px-5 py-3 md:py-4 text-white text-base placeholder-white/30 focus:outline-none transition-all duration-500 ${
                focusedField === 'phone' 
                  ? 'border-white/40 bg-white/10 shadow-lg' 
                  : 'border-white/10 hover:border-white/20'
              }`}
            />
            <div className={`absolute bottom-0 left-0 h-px bg-gradient-to-r from-white/40 to-transparent transition-all duration-500 ${
              focusedField === 'phone' ? 'w-full' : 'w-0'
            }`}></div>
          </div>

          {/* Email */}
          <div className={`relative transition-all duration-800 delay-700 ${
            isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'
          }`}>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField('')}
              required
              placeholder="Email Address*"
              className={`w-full bg-white/5 border rounded-xl px-5 py-3 md:py-4 text-white text-base placeholder-white/30 focus:outline-none transition-all duration-500 ${
                focusedField === 'email' 
                  ? 'border-white/40 bg-white/10 shadow-lg' 
                  : 'border-white/10 hover:border-white/20'
              }`}
            />
            <div className={`absolute bottom-0 left-0 h-px bg-gradient-to-r from-white/40 to-transparent transition-all duration-500 ${
              focusedField === 'email' ? 'w-full' : 'w-0'
            }`}></div>
          </div>

          {/* Program */}
          <div className={`relative transition-all duration-800 delay-800 ${
            isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'
          }`}>
            <input
              id="program"
              name="program"
              type="text"
              value={formData.program}
              readOnly
              required
              className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3 md:py-4 text-white/80 text-base focus:outline-none cursor-not-allowed"
            />
            <div className="absolute top-3 right-4 text-white/40 text-xs tracking-wide">
              SELECTED
            </div>
          </div>

          {/* Submit Button */}
          <div className={`transition-all duration-800 delay-900 ${
            isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'
          }`}>
            <button
              type="submit"
              disabled={submitting}
              className="group relative w-full bg-white text-black font-medium py-3 md:py-4 rounded-xl text-base tracking-[0.1em] uppercase hover:bg-white/95 transition-all duration-500 flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
            >
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              
              <Send size={18} className="transition-transform group-hover:scale-110" />
              <span className="relative z-10">
                {submitting ? "Submitting..." : "Join Waitlist"}
              </span>
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </form>

        {/* Footer note */}
        <div className={`mt-6 text-center transition-all duration-1000 delay-1000 ${
          isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'
        }`}>
          <p className="text-white/40 text-xs tracking-wide">
            <em>Fields marked with * are required</em>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Waitlist;